import { useEffect, useMemo, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getLegalDoc } from '../lib/legalContent';
import { renderLegalHtml, useLegalIdentity, useLegalTerms } from '../lib/legalIdentity';
import { openCookiePreferences } from '../lib/analyticsConsent';
import { buildFooterScheduleLines } from '../lib/scheduleDisplay';
import { useClinicBranding } from '../context/ClinicBrandingContext';
import './LegalPage.css';

export const LEGAL_PAGES = [
  { key: 'privacy', path: '/aviso-de-privacidad', label: 'Aviso de privacidad' },
  { key: 'terms', path: '/terminos', label: 'Términos y condiciones' },
  { key: 'cookies', path: '/cookies', label: 'Cookies' },
  { key: null, path: '/baja', label: 'Baja de promociones' },
];

/**
 * /aviso-de-privacidad, /terminos (anclas #demo, #promociones, #resenas) y /cookies.
 * Contenido: docs 01, 03 y 04 del paquete legal (lib/legalContent.js), con los datos de «Identidad y contacto»,
 * las condiciones de «Config tienda» (store.legal_terms) y el horario del panel. El 06 no se publica.
 */
export default function LegalPage({ docKey }) {
  const doc = getLegalDoc(docKey);
  const { identity, loading } = useLegalIdentity();
  const terms = useLegalTerms();
  const { schedule } = useClinicBranding();
  const location = useLocation();
  const navigate = useNavigate();
  const ref = useRef(null);

  const html = useMemo(() => {
    if (!doc) return '';
    const horario = schedule ? buildFooterScheduleLines(schedule).join('; ') : '';
    return renderLegalHtml(doc.html, { identity, terms, horario });
  }, [doc, identity, terms, schedule]);

  useEffect(() => {
    if (doc) document.title = doc.title;
  }, [doc]);

  // Anclas (/terminos#promociones) cuando el contenido ya está pintado.
  useEffect(() => {
    if (loading || !location.hash) return;
    const el = document.getElementById(decodeURIComponent(location.hash.slice(1)));
    if (el) el.scrollIntoView({ block: 'start' });
  }, [loading, location.hash, html]);

  // Enlaces internos del texto (/aviso-de-privacidad, /terminos#resenas…) sin recargar la página.
  const onClick = (e) => {
    const a = e.target.closest && e.target.closest('a');
    if (!a) return;
    const href = a.getAttribute('href') || '';
    if (href.startsWith('/') && !href.startsWith('//')) {
      e.preventDefault();
      navigate(href);
    }
  };

  if (!doc) return null;

  return (
    <div className="py-10 md:py-14" data-testid={`legal-page-${docKey}`}>
      <div className="max-w-4xl mx-auto px-4">
        <nav className="flex flex-wrap gap-2 mb-6 text-sm" aria-label="Documentos legales">
          {LEGAL_PAGES.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              aria-current={l.path === doc.path ? 'page' : undefined}
              className={`rounded-full border px-3 py-1 font-semibold transition-colors ${
                l.path === doc.path
                  ? 'bg-[#2B1A12] text-[#FFF6EA] border-[#2B1A12]'
                  : 'border-[#F1DFC8] text-[#6B5444] hover:text-[#C2410C]'
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>
        <article className="legal-content bg-[#FFFDF8] rounded-[22px] border border-[#F1DFC8] p-6 md:p-10">
          <h1>{doc.title}</h1>
          {docKey === 'cookies' ? (
            <p>
              <button
                type="button"
                onClick={openCookiePreferences}
                className="t-btn t-btn-ghost text-sm"
                data-testid="cookies-change-preferences"
              >
                Cambiar preferencias
              </button>
            </p>
          ) : null}
          {loading ? (
            <p className="animate-pulse text-[#6B5444]" data-testid="legal-page-loading">
              Cargando…
            </p>
          ) : (
            // Los valores se escapan en renderLegalHtml; el resto es texto fijo del paquete legal.
            <div ref={ref} onClick={onClick} dangerouslySetInnerHTML={{ __html: html }} />
          )}
        </article>
      </div>
    </div>
  );
}
